import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

export interface CRXTrackingParams {
  crx_company_id: string | null;
  crx_redirect_id: string | null;
}

export interface CRXConfirmationPixelParams extends CRXTrackingParams {
  reservation_number: string;
  total_charge: string;
  currency: string;
  pickup_time: string;
  dropoff_time: string;
  pickup_loc_id?: string;
  dropoff_loc_id?: string;
}

const SHARED_COOKIE_HOSTS = ['car-bro.com', 'www.car-bro.com'];

/**
 * Cookie domain for the crx cookie. The app is served on both car-bro.com and www.car-bro.com,
 * so the landing and the payment return can be on different hosts. Host-only elsewhere.
 */
export function crxCookieDomain(hostname: string): string | null {
  return SHARED_COOKIE_HOSTS.includes(hostname) ? '.car-bro.com' : null;
}

@Injectable({
  providedIn: 'root'
})
export class CrxTrackingService {
  private readonly COOKIE_NAME = 'crx_tracking';
  private readonly COOKIE_EXPIRY_DAYS = 30;

  constructor() {}

  /**
   * Captures CRX tracking parameters from URL query params and stores them in a cookie
   * Should be called on the initial landing page when user arrives from CRX redirect
   */
  captureTrackingParams(queryParams: { [key: string]: string }): void {
    const crx_company_id = queryParams['crx_company_id'];
    const crx_redirect_id = queryParams['crx_redirect_id'];

    if (crx_company_id || crx_redirect_id) {
      const trackingData: CRXTrackingParams = {
        crx_company_id: crx_company_id || null,
        crx_redirect_id: crx_redirect_id || null,
      };

      this.setTrackingCookie(trackingData);
    }
  }

  /**
   * Retrieves stored CRX tracking parameters from cookie
   */
  getTrackingParams(): CRXTrackingParams | null {
    const cookieValue = this.getCookie(this.COOKIE_NAME);
    if (!cookieValue) {
      return null;
    }

    try {
      return JSON.parse(decodeURIComponent(cookieValue));
    } catch (e) {
      console.error('Failed to parse CRX tracking cookie:', e);
      return null;
    }
  }

  /**
   * Fires the CRX confirmation tracking pixel
   * Should be called on the reservation confirmation/voucher page
   */
  fireConfirmationPixel(params: CRXConfirmationPixelParams): void {
    const trackingParams = this.getTrackingParams();

    // Only fire pixel if we have CRX tracking parameters
    if (!trackingParams?.crx_company_id || !trackingParams?.crx_redirect_id) {
      console.log('CRX tracking parameters not found, skipping confirmation pixel');
      return;
    }

    // Build query string
    const queryParams = new URLSearchParams({
      crx_company_id: trackingParams.crx_company_id,
      crx_redirect_id: trackingParams.crx_redirect_id,
      reservation_number: params.reservation_number,
      total_charge: params.total_charge,
      currency: params.currency,
      pickup_time: params.pickup_time,
      dropoff_time: params.dropoff_time,
    });

    // Add optional parameters
    if (params.pickup_loc_id) {
      queryParams.append('pickup_loc_id', params.pickup_loc_id);
    }
    if (params.dropoff_loc_id) {
      queryParams.append('dropoff_loc_id', params.dropoff_loc_id);
    }

    // Create and append tracking pixel
    const pixelUrl = `https://api.carrentalexpress.com/reservations/pixel/confirm?${queryParams.toString()}`;
    this.loadTrackingPixel(pixelUrl);

    // One conversion per click: a voucher reload or a later booking must not fire again
    this.clearTrackingParams();

    console.log('CRX confirmation pixel fired:', pixelUrl);
  }

  /**
   * Fires the CRX cancellation tracking pixel
   * Should be called on the cancellation confirmation page
   */
  fireCancellationPixel(reservationNumber: string, crxCompanyId: string): void {
    const queryParams = new URLSearchParams({
      crx_company_id: crxCompanyId,
      reservation_number: reservationNumber,
    });

    const pixelUrl = `https://api.carrentalexpress.com/reservations/pixel/cancel?${queryParams.toString()}`;
    this.loadTrackingPixel(pixelUrl);

    console.log('CRX cancellation pixel fired:', pixelUrl);
  }

  /**
   * Creates an invisible image element to load the tracking pixel
   */
  private loadTrackingPixel(url: string): void {
    const img = document.createElement('img');
    img.src = url;
    img.style.display = 'none';
    img.style.border = 'none';
    img.width = 1;
    img.height = 1;
    document.body.appendChild(img);

    // Remove after loading to keep DOM clean
    img.onload = () => {
      setTimeout(() => {
        document.body.removeChild(img);
      }, 1000);
    };
    img.onerror = () => {
      console.error('Failed to load CRX tracking pixel:', url);
      setTimeout(() => {
        document.body.removeChild(img);
      }, 1000);
    };
  }

  /**
   * Stores tracking parameters in a cookie
   */
  private setTrackingCookie(data: CRXTrackingParams): void {
    // Drop any older copy first, so reads never pick up a stale host-only cookie
    this.clearTrackingParams();

    const expires = new Date();
    expires.setDate(expires.getDate() + this.COOKIE_EXPIRY_DAYS);

    const cookieValue = encodeURIComponent(JSON.stringify(data));
    document.cookie = `${this.COOKIE_NAME}=${cookieValue}; expires=${expires.toUTCString()}; path=/${this.domainAttribute()}; SameSite=Lax`;
  }

  private domainAttribute(): string {
    const domain = crxCookieDomain(window.location.hostname);
    return domain ? `; domain=${domain}` : '';
  }

  /**
   * Retrieves a cookie value by name
   */
  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }

    return null;
  }

  /**
   * Clears the tracking cookie
   */
  clearTrackingParams(): void {
    const expired = `${this.COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
    // Host-only copy (set before the shared domain was used) and the shared-domain copy
    document.cookie = expired;
    if (this.domainAttribute()) {
      document.cookie = expired + this.domainAttribute();
    }
  }
}
